/**
 * OmegaChainPage — /omega/chain (S52 Go Live).
 *
 * Founder-only (FounderGuard route). English only, responsive from 375px.
 * Mirrors the S47/S49 omega pages: apiFetch + useQuery/useMutation + cn.
 *
 * The Autonomous Business Chain control screen: the 8 agents as a map (runs +
 * status), the T3 Founder approval queue (judgment / money / autonomy-flip
 * actions awaiting a signed approval), and chain totals. Every number paired
 * with words (5-field human text); autonomous action is off by default.
 *
 * Data:
 *   GET  /api/v1/admin/chain/summary   -> { agents[8], pending_approvals, total_cost_cents, total_runs }
 *   GET  /api/v1/admin/chain/approvals -> [{ id, agent, action, tier, rationale, projected_cost_cents }]
 *   POST /api/v1/admin/chain/approvals/{id}/sign | /reject
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/api/http';
import { cn } from '@/utils/cn';

interface AgentActivity {
  agent: string;
  runs: number;
  last_status: string;
}
interface ChainSummary {
  agents: AgentActivity[];
  pending_approvals: number;
  total_cost_cents: number;
  total_runs: number;
  generated_at?: string;
}
interface PendingApproval {
  id: number;
  agent: string;
  action: string;
  tier: string;
  rationale: string;
  projected_cost_cents: number;
}

// The 8 agents in pipeline order: human label, what they do, and the default
// action a Run click triggers. T1/T2 actions execute; T3 (strategy/campaign)
// park in the approval queue.
const AGENT_META: Record<string, { label: string; what: string; action: string }> = {
  research: { label: 'Research', what: 'Sizes the market (TAM/SAM/SOM) and ranks opportunities — read-only.', action: 'research_brief' },
  voc_pain: { label: 'VoC / Pain', what: 'Mines and validates real customer pains from reviews and tickets.', action: 'mine_pains' },
  ci: { label: 'Competitive Intel', what: 'Watches competitors for material changes and builds battlecards.', action: 'competitor_scan' },
  strategy: { label: 'Strategy', what: 'Proposes positioning, pricing and channels — judgment layer, you approve.', action: 'set_positioning' },
  content: { label: 'Content', what: 'Drafts evidence-grounded copy and pages; publishing needs your go-ahead.', action: 'draft_content' },
  campaign: { label: 'Campaign', what: 'Runs ads inside hard guardrails; scaling spend needs your approval.', action: 'scale_spend' },
  sales: { label: 'Sales', what: 'Works inbound first; cold outreach needs your approval and deliverability checks.', action: 'inbound_sequence' },
  optimization: { label: 'Optimization', what: 'Re-measures the funnel and reallocates budget toward what works.', action: 'optimize_within_envelope' },
};
const AGENT_ORDER = ['research', 'voc_pain', 'ci', 'strategy', 'content', 'campaign', 'sales', 'optimization'];

function fmtUsd(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function statusDot(status: string): string {
  if (status === 'executed' || status === 'done') return 'bg-success';
  if (status === 'idle') return 'bg-ink-subtle';
  return 'bg-amber-500';
}

export function OmegaChainPage() {
  const qc = useQueryClient();
  const summary = useQuery({
    queryKey: ['omega', 'chain', 'summary'],
    queryFn: () => apiFetch<ChainSummary>('/admin/chain/summary'),
    refetchInterval: 15_000,
    retry: false,
  });
  const approvals = useQuery({
    queryKey: ['omega', 'chain', 'approvals'],
    queryFn: () => apiFetch<PendingApproval[]>('/admin/chain/approvals'),
    refetchInterval: 15_000,
    retry: false,
  });

  const decide = useMutation({
    mutationFn: ({ id, approve }: { id: number; approve: boolean }) =>
      apiFetch(`/admin/chain/approvals/${id}/${approve ? 'sign' : 'reject'}`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['omega', 'chain', 'approvals'] });
      void qc.invalidateQueries({ queryKey: ['omega', 'chain', 'summary'] });
    },
  });

  // Run one agent's default action through the tier gate (T1/T2 execute; T3
  // parks for approval). apiFetch carries the founder JWT automatically.
  const runAgent = useMutation({
    mutationFn: ({ agent, action }: { agent: string; action: string }) =>
      apiFetch('/admin/chain/run', { method: 'POST', body: { agent, action } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['omega', 'chain', 'summary'] });
      void qc.invalidateQueries({ queryKey: ['omega', 'chain', 'approvals'] });
    },
  });

  const data = summary.data;
  const byAgent = new Map((data?.agents ?? []).map((a) => [a.agent, a]));
  const queue = approvals.data ?? [];

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Autonomous Business Chain</h1>
        <p className="text-sm text-ink-muted">
          Research → pain → competitor → strategy → content → campaign → sales → optimization. OMEGA runs
          the mechanical work; judgment, money and autonomy-flip actions wait for you (T3).
        </p>
        {data && (
          <p className="text-[11px] text-ink-subtle">
            {data.total_runs.toLocaleString()} actions · {fmtUsd(data.total_cost_cents)} spent ·{' '}
            {data.pending_approvals} awaiting your approval
          </p>
        )}
      </header>

      {/* T3 approval queue — the judgment gate */}
      <section className="flex flex-col gap-3">
        <h2 className="font-mono text-xs font-semibold uppercase tracking-wider text-ink-subtle">
          Decisions awaiting you (T3)
        </h2>
        <div className="rounded-lg border border-border bg-canvas p-4">
          {queue.length === 0 ? (
            <p className="text-sm text-ink-muted">
              Nothing waiting. OMEGA parks any judgment-layer, real-money or autonomy-flip action here for
              your signature before it can run.
            </p>
          ) : (
            <ul className="flex flex-col gap-3">
              {queue.map((a) => (
                <li key={a.id} className="flex flex-col gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
                      {a.tier}
                    </span>
                    <span className="text-sm font-semibold text-ink">{AGENT_META[a.agent]?.label ?? a.agent}</span>
                    <span className="font-mono text-xs text-ink-muted">{a.action}</span>
                    {a.projected_cost_cents > 0 && (
                      <span className="font-mono text-xs text-ink-muted">· {fmtUsd(a.projected_cost_cents)}</span>
                    )}
                  </div>
                  <p className="text-sm text-ink-muted">{a.rationale}</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => decide.mutate({ id: a.id, approve: true })}
                      disabled={decide.isPending}
                      className="min-h-[36px] rounded-md border border-success bg-success/10 px-3 text-sm font-semibold text-success hover:bg-success/20 disabled:opacity-60"
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => decide.mutate({ id: a.id, approve: false })}
                      disabled={decide.isPending}
                      className="min-h-[36px] rounded-md border border-border px-3 text-sm font-semibold text-ink-muted hover:bg-surface disabled:opacity-60"
                    >
                      Reject
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Chain map — the 8 agents */}
      <section className="flex flex-col gap-3">
        <h2 className="font-mono text-xs font-semibold uppercase tracking-wider text-ink-subtle">
          The chain
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {AGENT_ORDER.map((id) => {
            const meta = AGENT_META[id];
            const act = byAgent.get(id);
            const runs = act?.runs ?? 0;
            const status = act?.last_status ?? 'idle';
            return (
              <div key={id} className="flex flex-col gap-2 rounded-lg border border-border bg-canvas p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={cn('h-2 w-2 shrink-0 rounded-full', statusDot(status))} aria-hidden />
                  <h3 className="text-base font-semibold text-ink">{meta?.label ?? id}</h3>
                  <span className="font-mono text-sm tabular-nums text-ink">{runs.toLocaleString()}</span>
                  <span className="rounded-full border border-border bg-surface px-2 py-0.5 text-[10px] capitalize text-ink-subtle">
                    {status}
                  </span>
                </div>
                <p className="text-sm leading-snug text-ink-muted">{meta?.what}</p>
                <p className="text-xs text-ink-subtle">
                  {runs === 0
                    ? 'No actions yet.'
                    : `${runs.toLocaleString()} action${runs === 1 ? '' : 's'} taken; most recent: ${status}.`}
                </p>
                <button
                  type="button"
                  onClick={() => runAgent.mutate({ agent: id, action: meta?.action ?? '' })}
                  disabled={runAgent.isPending && runAgent.variables?.agent === id}
                  className="mt-1 min-h-[36px] self-start rounded-md border border-spark bg-spark/10 px-4 text-sm font-semibold text-spark transition-colors hover:bg-spark/20 disabled:opacity-60"
                >
                  {runAgent.isPending && runAgent.variables?.agent === id ? 'Running…' : 'Run'}
                </button>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

export default OmegaChainPage;
